import { sanitizePath, sanitizeQueryValue } from './sanitize';

// First-touch campaign attribution (#1458). Captured from the landing URL into
// memory on every page load, and persisted to localStorage only once consent
// allows: campaign values need statistics consent, ad-click ids also need
// marketing consent. Values are cleaned to the limits auth enforces.

export interface Touch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  landing_path?: string;
  referrer_host?: string;
  captured_at: string;
}

export const ATTRIBUTION_STORAGE_KEY = 'reed_attribution_v1';
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;
export const CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;

const HOST_RE = /^[a-z0-9.-]{1,255}$/;

function referrerHost(
  referrer: string,
  ownHost: string,
  ignoredHosts: readonly string[],
): string | undefined {
  try {
    const host = new URL(referrer).host.toLowerCase();
    if (!HOST_RE.test(host) || host === ownHost.toLowerCase() || ignoredHosts.includes(host)) {
      return undefined;
    }
    return host;
  } catch {
    return undefined;
  }
}

function setIfPresent<K extends keyof Touch>(
  touch: Touch,
  key: K,
  value: Touch[K] | undefined,
): void {
  if (value !== undefined) {
    touch[key] = value;
  }
}

/** Reads the landing URL's campaign parameters, sanitised path and referrer host. */
export function captureTouch(
  location: { readonly pathname: string; readonly search: string; readonly host: string },
  referrer: string,
  ignoredHosts: readonly string[],
  now: Date,
): Touch {
  const params = new URLSearchParams(location.search);
  const touch: Touch = { captured_at: now.toISOString() };

  for (const key of [...CAMPAIGN_KEYS, ...CLICK_KEYS]) {
    setIfPresent(touch, key, sanitizeQueryValue(key, params.get(key)));
  }

  const landingPath = sanitizePath(location.pathname);
  if (landingPath.startsWith('/') && landingPath.length <= 512) {
    touch.landing_path = landingPath;
  }
  setIfPresent(touch, 'referrer_host', referrerHost(referrer, location.host, ignoredHosts));
  return touch;
}

export function withoutClickIds(touch: Touch): Touch {
  const copy: Touch = { ...touch };
  for (const key of CLICK_KEYS) {
    delete copy[key];
  }
  return copy;
}

export function hasCampaign(touch: Touch): boolean {
  return [...CAMPAIGN_KEYS, ...CLICK_KEYS].some((key) => Boolean(touch[key]));
}

/**
 * Keeps the stored first touch; the current page's touch only fills ad-click
 * ids the stored touch lacks (they are dropped until marketing consent).
 */
export function mergeTouch(
  stored: Touch | null,
  current: Touch | null,
  marketing: boolean,
): Touch | null {
  // A stored direct visit (no campaign values) gives way to a later campaign
  // touch, so a returning ad click is not masked by an earlier direct visit.
  const first =
    stored && current && !hasCampaign(stored) && hasCampaign(current)
      ? current
      : (stored ?? current);
  let merged: Touch | null = first ? { ...first } : null;
  if (!merged) {
    return null;
  }

  if (current && first !== current && marketing) {
    for (const key of CLICK_KEYS) {
      if (!merged[key] && current[key]) {
        merged[key] = current[key];
      }
    }
  }
  if (!marketing) {
    merged = withoutClickIds(merged);
  }
  return merged;
}

function safeStorage(win: Window): Storage | null {
  try {
    return win.localStorage;
  } catch {
    return null;
  }
}

/** The stored first touch, or null when absent, malformed or outside the past 30 days. */
export function readStoredTouch(win: Window, now: Date): Touch | null {
  try {
    const raw = safeStorage(win)?.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<Record<keyof Touch, unknown>>;
    const capturedAt = typeof parsed.captured_at === 'string' ? new Date(parsed.captured_at) : null;

    if (
      !capturedAt ||
      Number.isNaN(capturedAt.getTime()) ||
      capturedAt.getTime() > now.getTime() ||
      now.getTime() - capturedAt.getTime() > ATTRIBUTION_TTL_MS
    ) {
      removeStoredTouch(win);
      return null;
    }

    const touch: Touch = { captured_at: capturedAt.toISOString() };
    for (const key of [...CAMPAIGN_KEYS, ...CLICK_KEYS]) {
      setIfPresent(
        touch,
        key,
        sanitizeQueryValue(key, typeof parsed[key] === 'string' ? parsed[key] : undefined),
      );
    }
    if (typeof parsed.landing_path === 'string' && parsed.landing_path.startsWith('/')) {
      touch.landing_path = sanitizePath(parsed.landing_path).slice(0, 512);
    }
    if (typeof parsed.referrer_host === 'string' && HOST_RE.test(parsed.referrer_host)) {
      touch.referrer_host = parsed.referrer_host;
    }
    return touch;
  } catch {
    return null;
  }
}

export function writeStoredTouch(win: Window, touch: Touch): void {
  try {
    safeStorage(win)?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // Storage blocked or full: attribution is best-effort.
  }
}

export function removeStoredTouch(win: Window): void {
  try {
    safeStorage(win)?.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing was stored.
  }
}
